<template lang="pug">
    section.content-section.directory-page
        .container-fluid
            .row
                .col-xl-7.order-2.order-xl-1
                    .row.justify-content-end.align-items-center.pe-3.results-for
                        .col-lg-12.col-xl-11.col-xxl-9
                            .row.align-items-center
                                .col-12.filter-search Results for: 
                                    .results-for__location {{ resultsFor }}
                    .row.justify-content-end.align-items-center.filters
                        .col-lg-12.col-xl-11.col-xxl-9
                            .row
                                .col-lg-4
                                    label.form-label(for="game") Game
                                    re-select(v-model="game" placeholder="Game" inputClass="form-select form-control")
                                        option(value="") All
                                        option(v-for="game in games" :value="game") {{ game }}
                                .col-lg-4
                                    label.form-label(for="game") Rating
                                    .rating-field.mt-1
                                        rating-stars(:rating="rating" @click="selectRating($event.star)" all)
                    .row.justify-content-end.pt-4.pb-4.result-cards
                        .col-lg-12.col-xl-11.col-xxl-9(v-if="gameMasters")
                            .row.row-cols-1.row-cols-md-2.g-4.games-listing(v-if="gameMasters.length")
                                .col(v-for="product in gameMasters" :key="product.id")
                                    game-master-card(:ref="`gameMasterCard${product.id}`" :product="product" :city-dash-state="cityDashState")
                            p(v-else) We couldn’t find any Game Masters in your region.
                .col-xl-5.order-1.order-xl-2.pr-0.pl-0(style="min-height: min(100vw, 100vh);")
                    GmapMap(ref="map" :center="mapCenter" :zoom="zoom" @bounds_changed="mapBoundsChanged" map-type-id="terrain" style="min-height: min(100vw, 100vh); width: 100%; height: 100%;")
                        GmapMarker(v-for="product in gameMasters" :key="product.id" :position="{ lat: product.location_latitude, lng: product.location_longitude }" :clickable="true" @click="clickMarker(product.id)" :icon="markerIcon(product.id)" @mouseover="markerMouseOver(product.id)" @mouseout="markerMouseOut(product.id)")

</template>

<script>
import GmapMap from 'gmap-vue/dist/components/map-layer'
import GmapMarker from 'gmap-vue/dist/components/marker-icon'

const SEARCH_TIMEOUT = 1000

export default {
    name: 'game-master-directory',

    components: {
        GmapMap,
        GmapMarker,
    },

    metaInfo() { return {
        title: this.metaTitle,
        meta: [
            { name: 'description', content: this.metaDescription },
            { name: 'og:title', content: `${this.metaTitle} - Lair DND` },
            { name: 'og:description', content: this.metaDescription },
        ],
    }},

    data() { return {
        resultsFor: '',
        fullAddress: '',
        mapCenter: {
            lat: 0,
            lng: 0,
        },
        zoom: 7,
        radius: 100,
        gameMasters: null,
        game: null,
        rating: 0,
        markersHover: {},
        searchableMapCenter: null,
        searching: false,
        searchTimeout: null,
    }},

    computed: {
        metaTitle() {
            return `Best Game Masters in ${this.fullAddress}`
        },
        metaDescription() {
            return `Results for Game Masters in your region.`
        },

        gmapApi() {
            return window.google
        },

        cityDashState() {
            return this.$route.params.cityDashState
        },

        games() {
            return window.addiesaas.lairdnd.games.map(game => game.title)
        },
    },

    watch: {
        cityDashState() {
            this.centerMap()
        },

        game() {
            this.searchGameMasters()
        },

        rating() {
            this.searchGameMasters()
        },
    },

    mounted() {
        this.$refs.map.$mapPromise.then(map => this.centerMap())
    },

    methods: {
        mapBoundsChanged(bounds) {
            if (typeof bounds != 'undefined') {
                this.searchableMapCenter = {
                    lat: bounds.getCenter().lat(),
                    lng: bounds.getCenter().lng(),
                }

                this.radius = this.getRadiusFromBounds(bounds)

                this.searchGameMasters()
            }
        },

        centerMap() {
            const service = new this.gmapApi.maps.places.PlacesService(this.$refs.map.$mapObject)

            const request = { query: this.cityDashState, fields: ['name', 'geometry', 'formatted_address'] }

            service.findPlaceFromQuery(request, (results, status) => {
                if (status === this.gmapApi.maps.places.PlacesServiceStatus.OK) {
                    const result = results[0]
                    this.$set(this.mapCenter, 'lat', result.geometry.location.lat())
                    this.$set(this.mapCenter, 'lng', result.geometry.location.lng())
                    this.zoom = this.getZoomFromViewport(result.geometry.viewport)
                    this.resultsFor = result.name
                    this.fullAddress = result.formatted_address
                }
            })
        },

        getZoomFromViewport(viewport) {
            const div = this.$refs.map.$mapObject.getDiv()
            const horizontalZoom = this.getDimensionZoom(viewport, 'lng', div.offsetWidth)
            const verticalZoom = this.getDimensionZoom(viewport, 'lat', div.offsetHeight)
            return Math.min(horizontalZoom, verticalZoom)
        },

        getDimensionZoom(viewport, dimension, pixelSize) {
            const GLOBE_WIDTH = 256

            const one = viewport.getSouthWest()[dimension]()
            const two = viewport.getNorthEast()[dimension]()
            let angle = two - one
            if (angle < 0) {
                angle += 360
            }

            return Math.floor(Math.log(pixelSize * 360 / angle / GLOBE_WIDTH) / Math.LN2)
        },

        getRadiusFromBounds(bounds) {
            const northEastDistance = this.getDistance(bounds.getCenter(), bounds.getNorthEast())
            const southWestDistance = this.getDistance(bounds.getCenter(), bounds.getSouthWest())

            return Math.max(northEastDistance, southWestDistance)
        },

        getDistance(locationA, locationB) {
            const EARTH_RADIUS = 6371

            const deg2rad = Math.PI / 180
            const latA = locationA.lat() * deg2rad
            const lngA = locationA.lng() * deg2rad
            const latB = locationB.lat() * deg2rad
            const lngB = locationB.lng() * deg2rad

            const lngDelta = lngB - lngA
            const a = Math.pow(Math.cos(latB) * Math.sin(lngDelta), 2) + Math.pow(Math.cos(latA) * Math.sin(latB) - Math.sin(latA) * Math.cos(latB) * Math.cos(lngDelta), 2)
            const b = Math.sin(latA) * Math.sin(latB) + Math.cos(latA) * Math.cos(latB) * Math.cos(lngDelta)

            const angle = Math.atan2(Math.sqrt(a), b)

            return angle * EARTH_RADIUS
        },

        async searchGameMasters() {
            if (this.searching) {
                if (this.searchTimeout) {
                    clearTimeout(this.searchTimeout)
                }

                this.searchTimeout = setTimeout(this.searchGameMasters, SEARCH_TIMEOUT)

                return
            }

            try {
                this.searching = true

                const { data: { data } } = await axios.post('/api/customer/game-master-reservation-product/search?include=user', {
                    latitude: this.searchableMapCenter.lat,
                    longitude: this.searchableMapCenter.lng,
                    radius: this.radius,
                    game: this.game,
                    rating: this.rating,
                })

                this.gameMasters = data
            } finally {
                this.searching = false
            }
        },

        markerMouseOver(id) {
            this.$set(this.markersHover, id, true)
        },
        markerMouseOut(id) {
            this.$delete(this.markersHover, id)
        },
        markerIcon(id) {
            return this.markersHover[id] ?
                `${this.baseUri}/images/client/lairdnd/map-marker-hover.svg` :
                `${this.baseUri}/images/client/lairdnd/map-marker.svg`
        },
        clickMarker(id) {
            const card = this.$refs[`gameMasterCard${id}`][0]
            const top = card.$el.getBoundingClientRect().top
            window.scrollTo({ top, behavior: 'smooth' })
        },

        selectRating(rating) {
            this.rating = this.rating == rating ? 0 : rating
        },
    }
}
</script>
